import { Sparkles, ShieldCheck } from "lucide-react";
import { RunnerBreakdown } from "@/components/analysis/RunnerBreakdown";
import { WeekendPass } from "@/components/WeekendPass";
import { OutcomeBadge, outcomeOf } from "@/components/RaceResult";
import { ShareTipCard } from "@/components/ShareTipCard";
import { useMember } from "@/components/MemberProvider";
import { SilksIcon } from "@/components/SilksIcon";
import { TipPrice } from "@/components/TipPrice";

const TipSilk = ({ race, n, i }) => {
  const r = race.runners?.find((x) => x.number === n) || {};
  return <SilksIcon url={r.silk_url} colours={r.silk_colours} testid={`tip-silk-${i + 1}`} />;
};

export const TipsCard = ({ race, meeting, onUnlocked }) => {
  const { isAdmin } = useMember();
  const a = race.analysis;
  return (
    <section data-testid="tips-card" className="rounded-xl border border-blue-500/30 bg-[#131C31] p-5 shadow-[0_8px_24px_rgba(0,0,0,0.45)]">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-blue-400" />
        <h3 className="font-display font-bold text-lg">The Weekend Edge Tips</h3>
      </div>
      {a?.locked ? <WeekendPass weekend={a.weekend} count={a.count} onUnlocked={onUnlocked} /> : !a ? (
        <p data-testid="tips-not-published" className="text-sm text-slate-400">{race.result ? "No tips were published for this race." : "Tips for this race haven't been published yet. Check back closer to the jump."}</p>
      ) : (
        <div className="space-y-4 text-sm" data-testid="tips-published">
          <ol className="space-y-2">
            {a.selections.map((s, i) => (
              <li key={i} data-testid={`tip-selection-${i + 1}`} className={`rounded-lg bg-[#0B101D]/70 border p-3 ${i === 0 ? "border-amber-400/40" : "border-[#202E4C]"}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-sm font-extrabold ${i === 0 ? "bg-amber-400 text-slate-950" : "bg-[#1B2640] border border-blue-500/40 text-blue-300"}`}>{i + 1}</span>
                  <TipSilk race={race} n={s.number} i={i} />
                  <span className="font-display font-bold text-slate-50 min-w-0 truncate">{s.number}. {s.horse}</span>
                  <TipPrice s={s} testid={`tip-price-${i + 1}`} />
                  <span className="ml-auto"><OutcomeBadge outcome={outcomeOf(s.number, race.result)} testid={`tip-outcome-${i + 1}`} /></span>
                </div>
                {s.note && <p className="text-xs text-slate-300 mt-2 leading-relaxed">{s.note}</p>}
              </li>
            ))}
          </ol>
          {isAdmin && meeting && <ShareTipCard meeting={meeting} race={race} />}
          {a.recommendation && (
            <div className="rounded-lg bg-blue-950/30 border border-blue-500/20 p-3" data-testid="tips-recommendation">
              <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold mb-1.5">Analyst view</p>
              <div className="space-y-2 text-slate-200 leading-relaxed">{a.recommendation.split(/\n\s*\n/).map((p, i) => <p key={i} className="whitespace-pre-wrap">{p.trim()}</p>)}</div>
            </div>
          )}
          {a.runners?.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-xs uppercase tracking-widest text-blue-400" data-testid="tips-breakdown-toggle">Form breakdown (from the data)</summary>
              <div className="space-y-3 mt-3">{a.runners.map((r) => <RunnerBreakdown key={r.number} r={r} compact />)}</div>
            </details>
          )}
          <p className="text-[11px] text-slate-500 flex items-start gap-1 leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>Reviewed & approved by The Weekend Edge. Opinion only, never a guarantee. Gamble responsibly. See our <a href="/terms" className="underline">Terms</a>.</span>
          </p>
        </div>
      )}
    </section>
  );
};
